import {
  InputField,
  DateInput,
  DropDownInput,
  Button,
  DataTable,
} from "@/components";
import { RightWrapper } from "../../../components";
import { useState, useCallback, useMemo, useEffect } from "react";
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilteredData } from "@/";
import { RiFileExcel2Line } from "react-icons/ri";
import { fetchWalletTransaction } from "../../../app/slices";
import { useSelector, useDispatch } from "react-redux";
import { subDays } from "date-fns";
const WalletTransaction = () => {
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const [filters, setFilters] = useState({
    shippingId: "",
    descriptionType: "",
    utrReference: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const dispatch = useDispatch();
  const fetchData = async () => {
    const response = await dispatch(
      fetchWalletTransaction({
        startDate: filters.date.from,
        endDate: filters.date.to,
      })
    );
    console.log("response::::::", response);
  };
  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  const WalletTransaction = useSelector(
    (state) => state.walletTransaction.walletTransactions
  );

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      shippingId: {
        type: "comma",
        itemKey: "shippingId",
      },
      descriptionType: { type: "exact", itemKey: "description" },
      utrReference: { type: "partial", itemKey: "utr" },
      date: { type: "date", itemKey: "date" },
    }),
    []
  );

  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    WalletTransaction,
    appliedFilters,
    filterConfig
  );
  const WalletIsLoading = useSelector(
    (state) => state.walletTransaction.loading
  );
  const isLocalLoading = isLoading || WalletIsLoading;

  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  // Memoized table columns
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Date",
        accessorKey: "date",
        cell: (info) => formatDate(info.row.original.date),
        meta: {
          tooltip: (row) => formatDate(row.date),
        },
      }),
      ColumnHelper({
        header: "Shipping ID",
        accessorKey: "shippingId",
        meta: {
          tooltip: (row) => row.shippingId || "N/A",
        },
      }),
      ColumnHelper({
        header: "UTR / Reference",
        accessorKey: "utr",
        meta: {
          tooltip: (row) => row.utr || "N/A",
        },
      }),
      ColumnHelper({
        header: "Credit",
        accessorKey: "credit",
        cell: (info) =>
          info.row.original.type === "Credit" ? (
            <span className="font-nunito">
              {formatCurrency(info.row.original.amount)}
            </span>
          ) : (
            "----"
          ),
        meta: {
          tooltip: (row) =>
            row.type === "Credit" ? (
              <span className="font-nunito">{formatCurrency(row.amount)}</span>
            ) : (
              "----"
            ),
        },
      }),
      ColumnHelper({
        header: "Debit",
        accessorKey: "Debit",
        cell: (info) =>
          info.row.original.type === "Debit" ? (
            <span className="font-nunito">
              {formatCurrency(info.row.original.amount)}
            </span>
          ) : (
            "----"
          ),
        meta: {
          tooltip: (row) =>
            row.type === "Debit" ? (
              <span className="font-nunito">{formatCurrency(row.amount)}</span>
            ) : (
              "----"
            ),
        },
      }),
      ColumnHelper({
        header: "Closing Balance",
        accessorKey: "closingBalance",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.closingBalance)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.closingBalance),
        },
      }),
      ColumnHelper({
        header: "Description",
        accessorKey: "description",
        meta: {
          tooltip: (row) => row.description || "N/A",
        },
      }),
    ],
    []
  );

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      shippingId: "",
      descriptionType: "",
      utrReference: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);

  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
          {/* Date Filter */}
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />

          {/* Shipping ID Input */}
          <InputField
            label="Shipping ID"
            placeholder="Enter Shipping ID"
            name="shippingId"
            value={filters.shippingId}
            onChange={(value) => handleChange("shippingId", value)}
          />

          <InputField
            label="UTR / Reference"
            placeholder="Enter UTR / Reference no."
            name="utrReference"
            value={filters.utrReference}
            onChange={(value) => handleChange("utrReference", value)}
          />

          {/* Description Type Dropdown */}
          <DropDownInput
            label="Description Type"
            name="descriptionType"
            placeholder="Select Description Type"
            options={[
              {
                value: "Appointment Delivery Charges Applied",
                label: "Appointment Delivery Charges Applied",
              },
              {
                value: "Address Correction Charges",
                label: "Address Correction Charges",
              },
              { value: "COD Charges", label: "COD Charges" },
              { value: "COD Payment", label: "COD Payment" },
              {
                value: "COD Charges Reversed For RTO Shipment",
                label: "COD Charges Reversed For RTO Shipment",
              },
              {
                value: "COD Charges Reversed For Cancelled Shipment",
                label: "COD Charges Reversed For Cancelled Shipment",
              },
              {
                value: "CN Against Damaged Shipment",
                label: "CN Against Damaged Shipment",
              },
              {
                value: "CN Against Lost Shipment",
                label: "CN Against Lost Shipment",
              },
              {
                value: "CN Against Weight Dispute Refund",
                label: "CN Against Weight Dispute Refund",
              },
              { value: "Credit Note Refund", label: "Credit Note Refund" },
              {
                value: "Extra Weight Charges Applied",
                label: "Extra Weight Charges Applied",
              },
              {
                value: "Extra Weight COD Charges Applied",
                label: "Extra Weight COD Charges Applied",
              },
              {
                value: "Extra Weight Charges Refunded",
                label: "Extra Weight Charges Refunded",
              },
              { value: "Freight Charges", label: "Freight Charges" },
              {
                value: "Freight Charges Reversed For Cancelled Shipment",
                label: "Freight Charges Reversed For Cancelled Shipment",
              },
              {
                value: "Freight Deductions From COD Remittance",
                label: "Freight Deductions From COD Remittance",
              },
              {
                value: "GST Penalty from RTO for Shipment Invoice",
                label: "GST Penalty from RTO for Shipment Invoice",
              },
              {
                value: "Holiday/Weekend Delivery charge Applied",
                label: "Holiday/Weekend Delivery charge Applied",
              },
              { value: "ODA Freight Charges", label: "ODA Freight Charges" },
              { value: "Penalty Charges", label: "Penalty Charges" },
              {
                value: "Penalty Charges applied from Courier Partner",
                label: "Penalty Charges applied from Courier Partner",
              },
              {
                value: "Pickup Rescheduling Charges Applied",
                label: "Pickup Rescheduling Charges Applied",
              },
              {
                value: "Re-Attempt Delivery Charges Applied",
                label: "Re-Attempt Delivery Charges Applied",
              },
              {
                value: "Refund Of RTO Freight Charges",
                label: "Refund Of RTO Freight Charges",
              },
              {
                value: "Reversal Of CN Against Damage Shipment",
                label: "Reversal Of CN Against Damage Shipment",
              },
              {
                value: "Reversal Of CN Against Lost Shipment",
                label: "Reversal Of CN Against Lost Shipment",
              },
              {
                value: "RTO Freight Charges Applied",
                label: "RTO Freight Charges Applied",
              },
              {
                value: "RTO Extra Weight Charges Applied",
                label: "RTO Extra Weight Charges Applied",
              },
              {
                value: "RTO Extra Weight Charges Refunded",
                label: "RTO Extra Weight Charges Refunded",
              },
              { value: "TDS Settlement Done", label: "TDS Settlement Done" },
              {
                value: "Volume Discount Given",
                label: "Volume Discount Given",
              },
              { value: "Wallet Recharge", label: "Wallet Recharge" },
              { value: "Messaging Charges", label: "Messaging Charges" },
              {
                value: "Reverse Pickup Charges",
                label: "Reverse Pickup Charges",
              },
              {
                value: "Reverse Pickup Attempt Charges",
                label: "Reverse Pickup Attempt Charges",
              },
              { value: "B2B Shipment Charges", label: "B2B Shipment Charges" },
              {
                value: 'Adjustment: Recovery of duplicate COD remittance',
                label: 'Adjustment: Recovery of duplicate COD remittance',
              },
            ]}
            value={filters.descriptionType}
            onChange={(value) => handleChange("descriptionType", value)}
          />

          {/* Action Buttons */}
          <div className="flex gap-2 w-full md:w-fit justify-end items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button variant="outline" size="md" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>
        <DataTable
          columns={columns}
          tittle="Wallet Transaction"
          data={finalFilteredData}
          showCheckbox={false}
          isLoading={isLocalLoading}
          resetPage={resetPageCounter}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Wallet Transaction",
                filename: `wallet_transaction${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
    </RightWrapper>
  );
};

export default WalletTransaction;
